import { HomeReview, Property, ReviewResponse } from '../../types';

export const getStatusPriority = (status: string | null | undefined): number => {
  switch (status) {
    case 'completed':
      return 3;
    case 'in_progress':
      return 2;
    case 'not_started':
      return 1;
    default:
      return 0;
  }
};

const hasMeaningfulResponseData = (response: ReviewResponse | undefined): boolean => {
  if (!response) {
    return false;
  }

  if (getStatusPriority(response.status) > getStatusPriority('not_started')) {
    return true;
  }

  if (typeof response.notes === 'string' && response.notes.trim().length > 0) {
    return true;
  }

  if (response.responseData && Object.keys(response.responseData).length > 0) {
    return true;
  }

  if ((response.photos || []).length > 0) {
    return true;
  }

  return false;
};

const isEmptyNotStartedResponse = (response: ReviewResponse): boolean =>
  getStatusPriority(response.status) <= getStatusPriority('not_started') &&
  !hasMeaningfulResponseData(response);

export const mergeResponseWithTimestamp = (
  localResponse: ReviewResponse | undefined,
  apiResponse: ReviewResponse
): ReviewResponse => {
  if (!localResponse) {
    return apiResponse;
  }

  if (
    localResponse.syncStatus === 'pending' ||
    localResponse.syncStatus === 'syncing' ||
    localResponse.syncStatus === 'failed'
  ) {
    const preservedPhotos = localResponse.photos || apiResponse.photos;
    return { ...localResponse, photos: preservedPhotos };
  }

  if (isEmptyNotStartedResponse(apiResponse) && hasMeaningfulResponseData(localResponse)) {
    const preservedPhotos = localResponse.photos || apiResponse.photos;
    return { ...localResponse, photos: preservedPhotos };
  }

  const localUpdatedAt = localResponse.updatedAt
    ? new Date(localResponse.updatedAt).getTime()
    : 0;
  const apiUpdatedAt = apiResponse.updatedAt
    ? new Date(apiResponse.updatedAt).getTime()
    : 0;

  let selectedResponse: ReviewResponse;
  const timeDiff = Math.abs(apiUpdatedAt - localUpdatedAt);
  const GRACE_PERIOD_MS = 5000;

  if (timeDiff < GRACE_PERIOD_MS) {
    const localPriority = getStatusPriority(localResponse.status);
    const apiPriority = getStatusPriority(apiResponse.status);

    selectedResponse = localPriority > apiPriority ? localResponse : apiResponse;
  } else if (localUpdatedAt > apiUpdatedAt) {
    selectedResponse = localResponse;
  } else {
    selectedResponse = apiResponse;
  }

  const preservedPhotos = selectedResponse.photos || localResponse.photos || apiResponse.photos;

  return {
    ...selectedResponse,
    photos: preservedPhotos,
  };
};

export const normalizeItemId = (id: number | string): string => {
  const idStr = String(id);
  return idStr.replace(/^synced_/, '');
};

export const extractOnDemandItems = (categories: any[] | undefined): any[] => {
  if (!categories || !Array.isArray(categories)) return [];

  const items: any[] = [];

  for (const category of categories) {
    if (category.items && Array.isArray(category.items)) {
      category.items.forEach((item: any) => {
        if (item.isOnDemand) {
          items.push(item);
        }
      });
    }

    if (category.subcategories && Array.isArray(category.subcategories)) {
      items.push(...extractOnDemandItems(category.subcategories));
    }
  }

  return items;
};

export const updateItemInCategories = (
  categories: any[] | undefined,
  itemId: number | string,
  updatedItem: any
): any[] | null => {
  if (!categories || !Array.isArray(categories)) return null;

  const normalizedItemId = normalizeItemId(itemId);
  let found = false;

  const newCategories = categories.map(category => {
    if (category.items && Array.isArray(category.items)) {
      const itemIndex = category.items.findIndex(
        (item: any) => normalizeItemId(item.id) === normalizedItemId
      );

      if (itemIndex !== -1) {
        found = true;
        return {
          ...category,
          items: category.items.map((item: any, i: number) =>
            i === itemIndex ? { ...item, ...updatedItem } : item
          ),
        };
      }
    }

    if (category.subcategories) {
      const updatedSubcategories = updateItemInCategories(
        category.subcategories,
        itemId,
        updatedItem
      );
      if (updatedSubcategories) {
        found = true;
        return {
          ...category,
          subcategories: updatedSubcategories,
        };
      }
    }

    return category;
  });

  return found ? newCategories : null;
};

export const mergeReviewData = (
  localReview: HomeReview | undefined,
  apiReview: HomeReview
): HomeReview => {
  if (!localReview) {
    return apiReview;
  }

  const localHasCategories = (localReview.template?.categories?.length || 0) > 0;
  const apiHasCategories = (apiReview.template?.categories?.length || 0) > 0;

  const localResponseMap = new Map(
    (localReview.responses || []).map(response => [response.itemId, response])
  );
  const mergedResponses = (apiReview.responses || []).map(apiResponse => {
    const localResponse = localResponseMap.get(apiResponse.itemId);
    return mergeResponseWithTimestamp(localResponse, apiResponse);
  });

  localReview.responses?.forEach(localResponse => {
    const existsInApi = apiReview.responses?.some(response => response.itemId === localResponse.itemId);
    if (!existsInApi) {
      mergedResponses.push(localResponse);
    }
  });

  const hasPendingReviewUpdate =
    localReview.syncStatus === 'pending' ||
    localReview.syncStatus === 'syncing' ||
    localReview.syncStatus === 'failed';

  const reviewLevelFields = ['notes', 'onDemandProjects', 'vendorNotes'] as const;
  const pendingFieldOverrides = reviewLevelFields.reduce((overrides, field) => {
    if (
      hasPendingReviewUpdate &&
      localReview[field] !== undefined &&
      apiReview[field] !== localReview[field]
    ) {
      overrides[field] = localReview[field];
    }

    return overrides;
  }, {} as Pick<HomeReview, 'notes' | 'onDemandProjects' | 'vendorNotes'>);

  const hasPendingFieldOverrides = Object.keys(pendingFieldOverrides).length > 0;

  return {
    ...apiReview,
    ...pendingFieldOverrides,
    syncStatus: hasPendingFieldOverrides
      ? localReview.syncStatus
      : apiReview.syncStatus,
    responses: mergedResponses,
    template: localHasCategories && !apiHasCategories
      ? localReview.template
      : apiReview.template,
    property: (apiReview.property || localReview.property) ? {
      ...(localReview.property || {}),
      ...(apiReview.property || {}),
      address: apiReview.property?.address ||
        apiReview.address ||
        localReview.property?.address ||
        localReview.address ||
        '',
    } as Property : undefined,
    address: apiReview.address ||
      apiReview.property?.address ||
      localReview.address ||
      localReview.property?.address ||
      undefined,
  };
};
